import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import { ClockCircleOutlined } from '@ant-design/icons';
import { 
  Form, 
  message, 
  Row, 
  Col,
  DatePicker,
  Table,
  Button,
  Checkbox,
  Input,
  Space,
  Alert,
  Spin
} from 'antd';
import fetchMiniList from '../../utils/miniFetcher.js';
import axiosInstance from '../../utils/axios.js';
import dayjs from 'dayjs';
import MovieCard from '../../components/dashboard/MovieCard.jsx';

const { RangePicker } = DatePicker;

const ShowUpdate = () => {
  const { id, movieId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [times, setTimes] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [slotData, setSlotData] = useState({});
  const [warnMsg, setWarnMsg] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [currentMovie, setCurrentMovie] = useState(null); // ← Changed to null for safety
  const [currentShow, setCurrentShow] = useState(null);   // ← Changed to null for safety
  const [loading, setLoading] = useState(true);           // ← Track full loading

  useEffect(() => {
    fetchMasters();
  }, [id, movieId]);

  const fetchMasters = async () => {
    setLoading(true);
    try {
      const timeResponse = await fetchMiniList("core/timeslot/");
      if (timeResponse.status) {
        setTimes(timeResponse.data || []);
      } else {
        message.error(timeResponse.data || "Failed to load timeslots");
      }

      const mRes = await fetchMiniList(`movie/movie/${movieId}`);
      if (mRes.status) {
        setCurrentMovie(mRes.data);
      } else {
        message.error(mRes.data || "Failed to load movie");
      }

      const sRes = await axiosInstance.get(`show-management/shows/${id}/`);
      setCurrentShow(sRes.data);
    } catch (error) {
      console.error("Error in fetchMasters:", error);
      message.error("Failed to fetch show details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentShow?.dates?.length > 0 && times.length > 0) {
      const startDate = dayjs(currentShow.dates[0].date);
      const endDate = dayjs(currentShow.dates[currentShow.dates.length - 1].date);

      form.setFieldsValue({
        date: [startDate, endDate]
      });

      const rows = currentShow.dates.map((d, idx) => ({
        key: d.date,
        serial: idx + 1,
        date: d.date,
        dateId: d.id,
        timeslots: times,
      }));
      setTableData(rows);

      const preFilled = {};
      currentShow.dates.forEach(d => {
        preFilled[d.date] = {};
        d.timeslots.forEach(ts => {
          preFilled[d.date][ts.timeslot] = {
            selectedId: ts.id,
            status: ts.status,
            publish: ts.publish || false,
            description: ts.description || ''
          };
        });
      });
      setSlotData(preFilled);
    }
  }, [currentShow, times, form]);

  const handleDateChange = (dates) => {
    if (!dates || dates.length !== 2) {
      setTableData([]);
      setSlotData({});
      return;
    }

    const [start, end] = dates;
    const dayCount = end.diff(start, 'day') + 1;

    let rows = [];
    for (let i = 0; i < dayCount; i++) {
      const date = start.add(i, 'day').format('YYYY-MM-DD');
      rows.push({
        key: date,
        serial: i + 1,
        date: date,
        timeslots: times
      });
    }
    setTableData(rows);
  };

  const handleSlotChange = (date, timeslotId, field, value) => {
    setSlotData(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        [timeslotId]: {
          ...prev[date]?.[timeslotId],
          [field]: value
        }
      }
    }));
  };

  const gotoRowPrices = (record, slotInfo) => {
    if (!currentMovie || !currentShow) {
      message.warning("Movie or show data not loaded yet");
      return;
    }

    navigate(`/admin/shows/row-prices/${id}/${record.dateId}/${slotInfo.selectedId}`, {
      state: {
        movie: currentMovie,
        show: currentShow,
        selected_slot: {
          date: record.date,
          date_id: record.dateId,
          timeslot: {
            id: slotInfo.selectedId,
            title: times.find(t => t.id === slotInfo.selectedId)?.title || 'N/A',
            start: times.find(t => t.id === slotInfo.selectedId)?.start_time,
            end: times.find(t => t.id === slotInfo.selectedId)?.end_time
          }
        }
      }
    });
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "serial",
      key: "serial",
      width: "8%",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "15%",
    },
    {
      title: "Timeslots",
      dataIndex: "timeslots",
      key: "timeslots",
      render: (timeslots, record) => (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {timeslots.map(t => {
            const slotInfo = slotData[record.date]?.[t.id] || {};
            return (
              <div key={t.id} style={{ 
                border: '1px solid #e8e8e8', 
                borderRadius: '8px',
                padding: '12px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
                  {t.title} ({t.start_time} - {t.end_time})
                </div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={slotInfo.status || false}
                    onChange={(e) => handleSlotChange(record.date, t.id, 'status', e.target.checked)}
                  >
                    Assign Show (for booking)
                  </Checkbox>
                  {/* <Checkbox
                    checked={slotInfo.publish || false}
                    onChange={(e) => handleSlotChange(record.date, t.id, 'publish', e.target.checked)}
                    disabled={!slotInfo.status}
                  >
                    Publish (visible to customers)
                  </Checkbox> */}
                  <Input
                    placeholder="Description (e.g., 2D, Subtitles)"
                    value={slotInfo.description || ''}
                    onChange={(e) => handleSlotChange(record.date, t.id, 'description', e.target.value)}
                    disabled={!slotInfo.status}
                    allowClear
                  />
                  {slotInfo.status && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => gotoRowPrices(record, slotInfo)}
                    >
                      Update Row Prices
                    </Button>
                  )}
                </Space>
              </div>
            );
          })}
        </Space>
      )
    }
  ];

  const handleFinish = async () => {
    setWarnMsg(null);
    setLoading(true);

    const result = {
      dates: tableData.map(row => ({
        date: dayjs(row.date).format('YYYY-MM-DD'),
        timeslots: times.map(t => {
          const info = slotData[row.date]?.[t.id] || {};
          return {
            id: t.id,
            status: info.status || false,
            publish: info.publish || false,
            description: info.description || ''
          };
        }).filter(t => t.status)
      })).filter(d => d.timeslots.length > 0)
    };

    try {
      const response = await axiosInstance.patch(`show-management/shows/${id}/`, result);
      messageApi.success("Show updated successfully");
    } catch (e) {
      console.error(e);
      const backendError = e.response?.data?.error || "Unexpected error occurred";
      messageApi.error(backendError);
      setWarnMsg(backendError);
    } finally {
      setLoading(false);
    }
  };

  // Loading state while fetching masters
  if (loading && !currentShow && !currentMovie) {
    return (
      <div className="dashboard" style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading show details...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {contextHolder}
      <PageHeading name="Edit Show" icon={<ClockCircleOutlined />} />

      {warnMsg && (
        <Alert message="Error" description={warnMsg} type="error" showIcon closable style={{ marginBottom: 16 }} />
      )}

      {/* Safe render MovieCard */}
      {currentMovie && currentShow ? (
        <MovieCard currentMovie={currentMovie} currentShow={currentShow} />
      ) : (
        <Alert
          message="Loading"
          description="Movie and show details are being loaded..."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="Very Important"
        description="Please do not choose past dates or uncheck timeslots if booking is ongoing. It may cause issues like booked shows disappearing from customer view."
        type="error"
        showIcon
        closable
        style={{ margin: '20px 0' }}
      />

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12} lg={8}>
            <Form.Item name="date" label="Dates" rules={[{ required: true }]}>
              <RangePicker onChange={handleDateChange} />
            </Form.Item>
          </Col>
        </Row>

        {tableData.length > 0 && (
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            bordered
            style={{ marginTop: 20 }}
            scroll={{ x: 'max-content' }}
          />
        )}

        <Form.Item style={{ marginTop: 30, textAlign: 'center' }}>
          <Button type="primary" htmlType="submit" loading={loading}>
            Update Show
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ShowUpdate;